// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';

import { FormLabelRequiredText } from '@zapier/design-system/src/components/forms/FormLabel';
import { Colors, Typography } from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

type Props = {|
  /**
   * The content to render within `FormLabel`. May be text or nodes containing
   * text, an `input`, etc.
   */
  children: Node,
  /**
   * The `for` attribute to attach to the `label` node, associating it to
   * an external input by its `id`.
   */
  htmlFor?: string,
  /**
   * Indicates whether the input the `FormLabel` controls is disabled.
   */
  isDisabled?: boolean,
  /**
   * Indicates whether the input the `FormLabel` controls is errored.
   */
  isErrored?: boolean,
  /**
   * Indicates whether the input the `FormLabel` controls is required.
   */
  isRequired?: boolean,
  /**
   * Indicates the layout for the content of the `Label`.
   * `text-only` stretches the text out as far as it will go and
   * expects `children` to be a single text node.
   * `auto-content` doesn't stretch anything and lays the content out
   * based on the content sizing.
   * When not specified, no layout is applied, so the `children`
   * supplied may need to provide their own layout for total customization.
   */
  layout?: 'text-only' | 'auto-content',
  /**
   * Size of the `FormLabel`.
   */
  size: 'small' | 'medium',
|};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      ...Typography.base,
      fontWeight: 600,
      color: Colors.black,
      cursor: 'pointer',
      display: 'block',
    },
    props.layout === 'text-only' &&
      withModifier('text-only', {
        display: 'grid',
        alignItems: 'center',
        gridGap: 10,
        // `1fr` to stretch text, `auto` for possible `required` node.
        gridTemplateColumns: '1fr auto',
      }),
    props.layout === 'auto-content' &&
      withModifier('auto-content', {
        display: 'grid',
        alignItems: 'center',
        gridGap: 10,
        // Create columns based on the supplied content.
        gridTemplateColumns: 'repeat(auto-fit, minmax(0, max-content))',
      }),
    withModifier(
      props.size,
      {
        small: {
          fontSize: 12,
          lineHeight: '20px',
        },
        medium: {
          fontSize: 14,
          lineHeight: '24px',
        },
      }[props.size]
    ),
    props.isErrored &&
      withModifier('errored', {
        color: Colors.red600,
      }),
    props.isDisabled &&
      withModifier('disabled', {
        cursor: 'not-allowed',
        color: Colors.grey700,
      }),
  ],
});

/**
 * `FormLabel` associates text to input fields.
 *
 * Tags: core-ui, forms
 */
const FormLabel = (props: Props) => {
  return (
    <label css={Styles.root(props)} htmlFor={props.htmlFor}>
      {props.children}
      {props.isRequired && <FormLabelRequiredText />}
    </label>
  );
};

FormLabel.defaultProps = {
  size: 'medium',
};

export default FormLabel;
